import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2, Pencil, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useContacts,
  useSaveContact,
  type Contact,
  type ContactFormValues,
  type ContactType,
} from "@/features/contacts";

type ContactTypeFilter = ContactType | "all";

const EMPTY_FORM: ContactFormValues = {
  taxId: "",
  name: "",
  tradeName: "",
  contactType: "both",
  giro: "",
  email: "",
  phone: "",
  address: "",
  region: "",
  province: "",
  comuna: "",
  purchaseCondition: "",
  saleCondition: "",
  juridical: true,
  foreign: false,
};

export function ContactosPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [contactType, setContactType] = useState<ContactTypeFilter>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [formValues, setFormValues] = useState<ContactFormValues>(EMPTY_FORM);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 300);

    return () => window.clearTimeout(timer);
  }, [search]);

  const { contacts, isLoading, hasLoaded, error, refetch } = useContacts({
    search: debouncedSearch || undefined,
    contactType,
  });
  const { saveContact, isSaving } = useSaveContact();
  const isInitialLoading = isLoading && !hasLoaded;
  const isRefreshing = isLoading && hasLoaded;

  const typeCounts = useMemo(
    () =>
      contacts.reduce(
        (counts, contact) => {
          counts[contact.contactType] += 1;
          return counts;
        },
        { customer: 0, supplier: 0, both: 0 },
      ),
    [contacts],
  );

  const openCreateDialog = () => {
    setEditingContact(null);
    setFormValues(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = (contact: Contact) => {
    setEditingContact(contact);
    setFormValues(toFormValues(contact));
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formValues.taxId.trim() || !formValues.name.trim()) {
      toast.error("RUT y razon social son obligatorios");
      return;
    }

    try {
      await saveContact(formValues, editingContact?.id);
      toast.success(
        editingContact ? "Contacto actualizado" : "Contacto creado",
      );
      setDialogOpen(false);
      refetch();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "No se pudo guardar el contacto",
      );
    }
  };

  if (isInitialLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 text-destructive">
        <AlertCircle className="h-8 w-8" />
        <p>Error al cargar los contactos</p>
        <p className="text-sm text-muted-foreground">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Contactos</h1>
          <p className="text-sm text-muted-foreground">
            {contacts.length} contactos encontrados
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo contacto
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por RUT, razon social o nombre fantasia..."
            className="pl-9"
          />
        </div>

        <Select
          value={contactType}
          onValueChange={(value) => setContactType(value as ContactTypeFilter)}
        >
          <SelectTrigger className="w-[170px]">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="customer">Clientes</SelectItem>
            <SelectItem value="supplier">Proveedores</SelectItem>
            <SelectItem value="both">Ambos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div
        className={
          isRefreshing
            ? "grid gap-3 opacity-60 transition-opacity sm:grid-cols-3"
            : "grid gap-3 transition-opacity sm:grid-cols-3"
        }
      >
        <Metric label="Clientes" value={typeCounts.customer} />
        <Metric label="Proveedores" value={typeCounts.supplier} />
        <Metric label="Ambos" value={typeCounts.both} />
      </div>

      <div className="relative rounded-lg border bg-card p-4">
        {isRefreshing && (
          <div className="absolute right-4 top-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Actualizando
          </div>
        )}
        <ContactsTable
          contacts={contacts}
          error={error}
          isLoading={isRefreshing}
          onEdit={openEditDialog}
        />
      </div>

      <ContactDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        values={formValues}
        onChange={setFormValues}
        isEditing={Boolean(editingContact)}
        isSaving={isSaving}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

function ContactsTable({
  contacts,
  error,
  isLoading,
  onEdit,
}: {
  contacts: Contact[];
  error: Error | null;
  isLoading: boolean;
  onEdit: (contact: Contact) => void;
}) {
  if (error) {
    return (
      <div className="py-8 text-center text-destructive">
        Error al actualizar los contactos: {error.message}
      </div>
    );
  }

  if (isLoading && contacts.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Cargando contactos
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No hay contactos
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b text-left text-sm text-muted-foreground">
            <th className="pb-2 font-medium">RUT</th>
            <th className="pb-2 font-medium">Razon social</th>
            <th className="pb-2 font-medium">Nombre fantasia</th>
            <th className="pb-2 font-medium">Tipo</th>
            <th className="pb-2 font-medium">Email</th>
            <th className="pb-2 font-medium">Telefono</th>
            <th className="pb-2 font-medium">Region / Comuna</th>
            <th className="w-10 pb-2 font-medium"></th>
          </tr>
        </thead>
        <tbody>
          {contacts.map((contact) => (
            <tr
              key={contact.id}
              className="group border-b transition-colors last:border-0 hover:bg-muted/50"
            >
              <td className="py-3 font-mono text-sm text-muted-foreground">
                {contact.taxId}
              </td>
              <td className="max-w-[260px] truncate py-3 text-sm">
                {contact.name}
              </td>
              <td className="max-w-[220px] truncate py-3 text-sm text-muted-foreground">
                {contact.tradeName || "-"}
              </td>
              <td className="py-3 text-sm">
                {formatContactType(contact.contactType)}
              </td>
              <td className="max-w-[220px] truncate py-3 text-sm text-muted-foreground">
                {stringMetadata(contact, "email") || "-"}
              </td>
              <td className="py-3 text-sm text-muted-foreground">
                {stringMetadata(contact, "phone") || "-"}
              </td>
              <td className="py-3 text-sm text-muted-foreground">
                {formatLocation(contact)}
              </td>
              <td className="py-3 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={() => onEdit(contact)}
                >
                  <Pencil className="h-4 w-4" />
                  <span className="sr-only">Editar</span>
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContactDialog({
  open,
  onOpenChange,
  values,
  onChange,
  isEditing,
  isSaving,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  values: ContactFormValues;
  onChange: (values: ContactFormValues) => void;
  isEditing: boolean;
  isSaving: boolean;
  onSubmit: () => void;
}) {
  const update = <K extends keyof ContactFormValues>(
    key: K,
    value: ContactFormValues[K],
  ) => onChange({ ...values, [key]: value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar contacto" : "Nuevo contacto"}
          </DialogTitle>
          <DialogDescription>
            Administra clientes y proveedores para procesos contables.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="RUT" required>
            <Input
              value={values.taxId}
              onChange={(event) => update("taxId", event.target.value)}
            />
          </Field>
          <Field label="Tipo">
            <Select
              value={values.contactType}
              onValueChange={(value) =>
                update("contactType", value as ContactType)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Cliente</SelectItem>
                <SelectItem value="supplier">Proveedor</SelectItem>
                <SelectItem value="both">Ambos</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Razon social" required>
            <Input
              value={values.name}
              onChange={(event) => update("name", event.target.value)}
            />
          </Field>
          <Field label="Nombre fantasia">
            <Input
              value={values.tradeName}
              onChange={(event) => update("tradeName", event.target.value)}
            />
          </Field>
          <Field label="Giro">
            <Input
              value={values.giro}
              onChange={(event) => update("giro", event.target.value)}
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={values.email}
              onChange={(event) => update("email", event.target.value)}
            />
          </Field>
          <Field label="Telefono">
            <Input
              value={values.phone}
              onChange={(event) => update("phone", event.target.value)}
            />
          </Field>
          <Field label="Direccion">
            <Input
              value={values.address}
              onChange={(event) => update("address", event.target.value)}
            />
          </Field>
          <Field label="Region">
            <Input
              value={values.region}
              onChange={(event) => update("region", event.target.value)}
            />
          </Field>
          <Field label="Provincia">
            <Input
              value={values.province}
              onChange={(event) => update("province", event.target.value)}
            />
          </Field>
          <Field label="Comuna">
            <Input
              value={values.comuna}
              onChange={(event) => update("comuna", event.target.value)}
            />
          </Field>
          <Field label="Condicion compra">
            <Input
              value={values.purchaseCondition}
              onChange={(event) =>
                update("purchaseCondition", event.target.value)
              }
            />
          </Field>
          <Field label="Condicion venta">
            <Input
              value={values.saleCondition}
              onChange={(event) => update("saleCondition", event.target.value)}
            />
          </Field>
          <div className="flex items-center gap-6 pt-6">
            <CheckboxField
              label="Juridica"
              checked={values.juridical}
              onCheckedChange={(checked) => update("juridical", checked)}
            />
            <CheckboxField
              label="Extranjero"
              checked={values.foreign}
              onCheckedChange={(checked) => update("foreign", checked)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
    </div>
  );
}

function CheckboxField({
  label,
  checked,
  onCheckedChange,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
      />
      {label}
    </label>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-2 text-xl font-semibold">{value}</p>
    </div>
  );
}

function toFormValues(contact: Contact): ContactFormValues {
  return {
    taxId: contact.taxId,
    name: contact.name,
    tradeName: contact.tradeName ?? "",
    contactType: contact.contactType,
    giro:
      stringMetadata(contact, "giro") ||
      stringMetadata(contact, "businessActivity"),
    email: stringMetadata(contact, "email"),
    phone: stringMetadata(contact, "phone"),
    address: stringMetadata(contact, "address"),
    region: stringMetadata(contact, "region"),
    province: stringMetadata(contact, "province"),
    comuna: stringMetadata(contact, "comuna"),
    purchaseCondition: stringMetadata(contact, "purchaseCondition"),
    saleCondition: stringMetadata(contact, "saleCondition"),
    juridical: booleanMetadata(contact, "juridical", true),
    foreign: booleanMetadata(contact, "foreign", false),
  };
}

function stringMetadata(contact: Contact, key: string): string {
  const value = contact.countryMetadata?.[key];

  return typeof value === "string" ? value : "";
}

function booleanMetadata(
  contact: Contact,
  key: string,
  fallback: boolean,
): boolean {
  const value = contact.countryMetadata?.[key];

  return typeof value === "boolean" ? value : fallback;
}

function formatLocation(contact: Contact): string {
  const region = stringMetadata(contact, "region");
  const comuna = stringMetadata(contact, "comuna");

  return [region, comuna].filter(Boolean).join(" / ") || "-";
}

function formatContactType(value: ContactType): string {
  const labels: Record<ContactType, string> = {
    customer: "Cliente",
    supplier: "Proveedor",
    both: "Ambos",
  };

  return labels[value];
}
